import { useEffect, useRef, useState } from "react";
import api, { apiError } from "../lib/api";
import { PageHeader } from "../components/shared";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Upload, Trash2, ImageIcon, AlertTriangle, CheckCircle2, Search, RefreshCw } from "lucide-react";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "");
const src = (p) => (p ? (p.startsWith("http") ? p : `${BACKEND}${p}`) : "");
const KINDS = [["banners", "Home Banners"], ["offers", "Special Offers"], ["categories", "Categories"], ["services", "Services"], ["packages", "Packages"], ["staff", "Staff"]];
const OK_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

const readDims = (file) => new Promise((res) => { const img = new Image(); img.onload = () => { res({ w: img.naturalWidth, h: img.naturalHeight, url: img.src }); }; img.onerror = () => res(null); img.src = URL.createObjectURL(file); });

// Validate a picked file against the section spec -> list of warnings (soft) and errors (hard).
export function checkImage(file, dims, spec) {
  const errors = [], warnings = [];
  if (!OK_TYPES.includes(file.type)) errors.push("Only JPG, PNG, WEBP or GIF images are allowed.");
  if (file.size > 5 * 1024 * 1024) errors.push("File is over the 5 MB hard limit.");
  if (dims) {
    const want = spec.w / spec.h, got = dims.w / dims.h, dev = Math.abs(got - want) / want;
    if (dev > 0.03) warnings.push(`Aspect ratio is ${dims.w}×${dims.h} (${(got).toFixed(2)}:1) but this section needs ${spec.ratio} (${want.toFixed(2)}:1) — edges will be cropped.`);
    if (dims.w < spec.w * 0.75 || dims.h < spec.h * 0.75) warnings.push(`Image is ${dims.w}×${dims.h}px — smaller than the recommended ${spec.w}×${spec.h}px and may look blurry.`);
  }
  if (file.size > spec.kb * 1024 * 1.5) warnings.push(`File is ${Math.round(file.size / 1024)} KB — recommended ≤ ${spec.kb} KB for fast mobile loading.`);
  return { errors, warnings };
}

// Safe-area overlay drawn on top of the preview (matches how the public site overlays each image).
function SafeOverlay({ kind }) {
  const box = "absolute border-2 border-dashed border-yellow-300/90 bg-yellow-300/10";
  if (kind === "banners") return <><div className={`${box} inset-y-0 left-0 w-[60%]`} title="Site text zone" /><div className="absolute left-2 top-2 text-[10px] text-yellow-200 font-semibold drop-shadow">Website text zone — keep clear</div><div className="absolute inset-x-[20%] inset-y-0 border-x-2 border-white/60" title="Desktop crop" /></>;
  if (kind === "services") return <><div className={`${box} inset-x-0 top-0 h-[12%]`} /><div className="absolute left-2 top-1 text-[10px] text-yellow-200 font-semibold drop-shadow">Ribbon / % OFF zone</div></>;
  if (kind === "offers") return <><div className={`${box} left-0 bottom-0 w-[30%] h-[20%]`} /><div className="absolute left-2 bottom-1 text-[10px] text-yellow-200 font-semibold drop-shadow">Save ₹ pill</div></>;
  if (kind === "staff") return <div className="absolute inset-[5%] rounded-full border-2 border-dashed border-yellow-300/90" />;
  return <div className="absolute inset-[10%] border-2 border-dashed border-yellow-300/70" />;
}

function Preview({ kind, spec, url, className = "" }) {
  return (
    <div className={`relative overflow-hidden rounded-xl bg-[#1a1a1a] ${className}`} style={{ aspectRatio: `${spec.w} / ${spec.h}` }}>
      {url ? <img src={url} alt="" className={`h-full w-full object-${spec.fit}`} /> : <div className="h-full w-full grid place-items-center text-muted-foreground"><ImageIcon size={26} /></div>}
      {url && <SafeOverlay kind={kind} />}
    </div>
  );
}

function UploadDialog({ kind, spec, item, file, onClose, onDone }) {
  const [dims, setDims] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { readDims(file).then(setDims); }, [file]);
  const { errors, warnings } = checkImage(file, dims, spec);
  const upload = async () => {
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/website-images/${kind}/${item.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Image uploaded — live on the booking site"); onDone(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border max-w-lg" data-testid="img-upload-dialog">
        <DialogHeader><DialogTitle className="font-display">Upload for “{item.label}”</DialogTitle></DialogHeader>
        <Preview kind={kind} spec={spec} url={dims?.url} className="w-full" />
        <p className="text-xs text-muted-foreground">Dashed area = safe-area guide. {spec.safe}</p>
        {dims && <p className="text-xs" data-testid="img-dims">Your image: <b>{dims.w} × {dims.h}px</b> · {Math.round(file.size / 1024)} KB · Recommended: {spec.w} × {spec.h}px ({spec.ratio}) ≤ {spec.kb} KB</p>}
        {errors.map((e) => <p key={e} className="text-sm text-destructive flex gap-2" data-testid="img-error"><AlertTriangle size={16} className="shrink-0" /> {e}</p>)}
        {warnings.map((w) => <p key={w} className="text-sm text-orange-300 flex gap-2" data-testid="img-warning"><AlertTriangle size={16} className="shrink-0" /> {w}</p>)}
        {!errors.length && !warnings.length && dims && <p className="text-sm text-green-400 flex gap-2" data-testid="img-ok"><CheckCircle2 size={16} /> Perfect fit — no cropping or quality loss.</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm" data-testid="img-cancel">Cancel</Button>
          <Button onClick={upload} disabled={busy || !!errors.length || !dims} className={`rounded-sm ${warnings.length ? "bg-orange-500 hover:bg-orange-600 text-white" : "bg-primary text-primary-foreground hover:bg-[#D4AF37]"}`} data-testid="img-confirm">
            {busy ? "Uploading…" : warnings.length ? "Upload anyway" : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ kind }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [pending, setPending] = useState(null);
  const inputs = useRef({});
  const load = () => api.get(`/website-images/${kind}`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const { spec, items } = data;
  const list = items.filter((i) => !q || i.label.toLowerCase().includes(q.toLowerCase()) || (i.sub || "").toLowerCase().includes(q.toLowerCase()));
  const remove = async (item) => { if (!window.confirm(`Remove image for "${item.label}"?`)) return; try { await api.delete(`/website-images/${kind}/${item.id}`); toast.success("Image removed"); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const missing = items.filter((i) => !i.image).length;
  return (
    <div data-testid={`img-section-${kind}`}>
      <Card className="p-4 bg-card border-border mb-4 flex flex-wrap items-center gap-4">
        <div className="text-sm" data-testid={`img-spec-${kind}`}>
          <p className="font-semibold text-primary">Recommended Image Size: {spec.w} × {spec.h} px</p>
          <p className="text-muted-foreground">Aspect ratio <b className="text-foreground">{spec.ratio}</b> · WEBP/JPG/PNG · ≤ {spec.kb} KB · displayed with object-fit: {spec.fit}</p>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">{spec.safe}</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Badge variant="outline" className={missing ? "border-orange-400 text-orange-300" : "border-green-500 text-green-400"} data-testid={`img-missing-${kind}`}>{missing ? `${missing} without image` : "All have images"}</Badge>
          <div className="relative"><Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="bg-secondary h-9 pl-8 w-44" data-testid={`img-search-${kind}`} /></div>
          <Button variant="ghost" size="sm" onClick={load} className="h-9"><RefreshCw size={14} /></Button>
        </div>
      </Card>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
        {list.map((item) => (
          <Card key={item.id} className="p-3 bg-card border-border flex flex-col gap-2" data-testid={`img-item-${item.id}`}>
            <Preview kind={kind} spec={spec} url={src(item.image || item.fallback)} className="w-full" />
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{item.label}</p>
              <p className="text-[11px] text-muted-foreground truncate">{item.sub || ""}{item.price != null ? ` · ₹${item.price}${item.mrp > item.price ? ` (MRP ₹${item.mrp})` : ""}` : ""}</p>
              {!item.image && item.fallback && <p className="text-[11px] text-orange-300">Using service image (no dedicated offer image)</p>}
            </div>
            <div className="flex gap-2 mt-auto">
              <input type="file" accept="image/*" className="hidden" ref={(el) => { inputs.current[item.id] = el; }} data-testid={`img-file-${item.id}`}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) setPending({ item, file: f }); e.target.value = ""; }} />
              <Button size="sm" onClick={() => inputs.current[item.id]?.click()} className="rounded-sm flex-1 bg-primary text-primary-foreground hover:bg-[#D4AF37]" data-testid={`img-upload-${item.id}`}><Upload size={14} className="mr-1" /> {item.image ? "Replace" : "Upload"}</Button>
              {item.image && <Button size="sm" variant="outline" onClick={() => remove(item)} className="rounded-sm text-destructive" data-testid={`img-remove-${item.id}`}><Trash2 size={14} /></Button>}
            </div>
          </Card>
        ))}
        {!list.length && <p className="col-span-full text-center text-sm text-muted-foreground py-10">{kind === "banners" ? "No banners yet — create banners in Booking Site → Promo Banners, then upload their images here." : "Nothing to show."}</p>}
      </div>
      {pending && <UploadDialog kind={kind} spec={spec} item={pending.item} file={pending.file} onClose={() => setPending(null)} onDone={() => { setPending(null); load(); }} />}
    </div>
  );
}

export default function WebsiteImages() {
  const [tab, setTab] = useState("banners");
  return (
    <div>
      <PageHeader title="Website Images" subtitle="Upload, replace or remove every image shown on the public booking website. Changes go live instantly." />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-secondary flex-wrap h-auto" data-testid="img-tabs">{KINDS.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`img-tab-${k}`}>{l}</TabsTrigger>)}</TabsList>
        {KINDS.map(([k]) => <TabsContent key={k} value={k}>{tab === k && <Section kind={k} />}</TabsContent>)}
      </Tabs>
    </div>
  );
}
